import { useMutation } from "@tanstack/react-query";
import { type Questionnaire } from "api-client/legacy-models";
import { type RouterInput } from "api-client/legacy-trpc";
import { useDebouncedCallback } from "use-debounce";

import { trpcClient } from "src/trpc";

import { useQuestionnaireSubmissionContext } from "../QuestionnaireSubmissionContext";

type Input = RouterInput["questionnaire"]["answer"];

export function useAnswerQuestion() {
  const mutation = useMutation({
    mutationFn: (arg: Questionnaire.Answer.Input) =>
      trpcClient.questionnaire.answer.mutate(arg),
  });
  const questionnaireSubmissionContext = useQuestionnaireSubmissionContext();

  const performCall = useDebouncedCallback(async (input: Input) => {
    const data = await mutation.mutateAsync(input);
    questionnaireSubmissionContext.onAnswered(data);
  }, 200);

  async function answerQuestion(
    input: Omit<Input, "questionnaireSubmissionId">,
  ) {
    const answer = input.questionId
      ? questionnaireSubmissionContext.allAnswersByQuestionId[input.questionId]
      : null;

    const foundAnswer =
      questionnaireSubmissionContext.allAnswersByQuestionId[input.questionId];

    if (!foundAnswer) {
      questionnaireSubmissionContext.createOptimisticAnswer({
        id: answer?.id || "creating",
        value: input.value || "",
        options: input.options || [],
        questionId: input.questionId,
        confirmed: input.confirmed || null,
        otherOptionValue: input.otherOptionValue || null,
      });
    } else {
      questionnaireSubmissionContext.updateAnswerByQuestionId(
        foundAnswer.questionId,
        input,
      );
    }

    await performCall({
      ...input,
      questionnaireSubmissionId:
        questionnaireSubmissionContext.questionnaireSubmission.id,
    });
  }

  return {
    mutation,
    perform: answerQuestion,
  };
}
